import { useApolloClient } from "@apollo/client";
import { useState } from "react";
import useSnackBars from "./useSnackbar";
import { OnResultDocument, useDownloadSelfAssessmentLazyQuery } from "../generated";
import DownloadFile from "../lib/utils/DownloadFile";
import useNewrelic from "./useNewrelic";

const useDownloadSelfAssessment = () => {
	const [loading, setLoading] = useState(false);
	const client = useApolloClient();
	const { setAlerts } = useSnackBars();
	const { noticeError } = useNewrelic();
	
	const [ downloadSelfAssessmentQuery ] = useDownloadSelfAssessmentLazyQuery({});
	
	const downloadSelfAssessment = async (
		arn: string,
		applicationName: string,
	) => {
		try {
			setLoading(true);
			const { data } = await downloadSelfAssessmentQuery({
				variables: { arn, frameworkName: applicationName },
				context: { apiName: "well_architected" },
				fetchPolicy: "no-cache",
			});

			const downloadId = data?.downloadSelfAssessment?.id;
			if (!downloadId) {
				throw new Error("No download ID returned");
			}

			const subscriptionPromise = await new Promise((resolve, reject) => {
				const observer = client
					.subscribe({
						query: OnResultDocument,
						variables: { id: downloadId },
					})
					.subscribe({
						next: ({ data }) => {
							if (data?.onResult?.status === "COMPLETE") {
								const decoded = JSON.parse(data?.onResult?.result);
								DownloadFile(decoded.url, decoded.filename);
								observer.unsubscribe();
								setLoading(false);
								resolve(true);
							} else if (data?.onResult?.status === "ERROR") {
								setLoading(false);
								setAlerts([{ severity: "error", msg: "Self-Assessment download failed" }]);
								observer.unsubscribe();
								reject(new Error("Download failed"));
							}
						},
						error: (err) => {
							console.error("Subscription error:", err);
							setLoading(false);
							noticeError(err, "Subscription error during download.");
							reject(err);
						},
					});
			});

			return { reportdownload: subscriptionPromise };
		} catch (err) {
			setLoading(false);
			noticeError(err, "Self-Assessment download failed.");
			setAlerts([{ severity: "error", msg: "Self-Assessment download failed" }]);
			return { reportdownload: false };
		}
	};

	return {
		downloadSelfAssessment,
		loading,
	};
};

export { useDownloadSelfAssessment };